/**
 * Pure helpers for reading breakdown rows without inflating totals.
 *
 * Two facts drive everything here:
 *
 * 1. `metrics.aggregate` is ONE flat array holding rows from every breakdown
 *    dimension (OS, browser, device type, country, city, region, UTM,
 *    referrer). City and region rows carry their parent `country`, and OS rows
 *    can be mapped to a device class, so any sum that doesn't first pick a
 *    single dimension counts the same visitors more than once.
 * 2. Unique visitors are not additive. A visitor appears in every row they
 *    touched (several pages, several page groups, a UTM row and a referrer
 *    row), so a sum of `unique_visitors` overstates the period. Shares are
 *    taken against the period's deduped total, never against a row sum.
 */

type AggregateRow = Record<string, any>

const toCount = (value: unknown): number => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

const regionOf = (row: AggregateRow): string | undefined => row.region || row.subdivision || undefined

/** A country-dimension row: has a country and no finer location dimension. */
export const isCountryRow = (row: AggregateRow): boolean => Boolean(row?.country) && !row.city && !regionOf(row)

/** A city-dimension row. */
export const isCityRow = (row: AggregateRow): boolean => Boolean(row?.city)

/** A region-dimension row (city rows excluded so a row never counts twice). */
export const isRegionRow = (row: AggregateRow): boolean => Boolean(regionOf(row)) && !row.city

export interface LocationCount {
  /** Aggregation key: ISO country code, or `name, country` for cities/regions */
  key: string
  /** City / region name, or the country code for countries */
  name: string
  /** Parent ISO country code */
  country: string
  count: number
}

const sortDesc = <T extends { count: number }>(rows: T[]) => rows.sort((a, b) => b.count - a.count)

/** Country totals from country-dimension rows only. */
export function aggregateCountries(aggregate: AggregateRow[] | null | undefined): LocationCount[] {
  const stats = new Map<string, number>()
  for (const row of aggregate ?? []) {
    if (!isCountryRow(row)) continue
    stats.set(row.country, (stats.get(row.country) ?? 0) + toCount(row.count))
  }
  return sortDesc([...stats].map(([code, count]) => ({ key: code, name: code, country: code, count })))
}

function aggregateSubdivision(
  aggregate: AggregateRow[] | null | undefined,
  match: (row: AggregateRow) => boolean,
  nameOf: (row: AggregateRow) => string,
): LocationCount[] {
  const stats = new Map<string, LocationCount>()
  for (const row of aggregate ?? []) {
    if (!match(row)) continue
    const name = nameOf(row)
    const country = row.country ?? ''
    const key = `${name}|${country}`
    const entry = stats.get(key) ?? { key, name, country, count: 0 }
    entry.count += toCount(row.count)
    stats.set(key, entry)
  }
  return sortDesc([...stats.values()])
}

/** City totals from city-dimension rows only. */
export const aggregateCities = (aggregate: AggregateRow[] | null | undefined) =>
  aggregateSubdivision(aggregate, isCityRow, (row) => String(row.city))

/** Region totals from region-dimension rows only. */
export const aggregateRegions = (aggregate: AggregateRow[] | null | undefined) =>
  aggregateSubdivision(aggregate, isRegionRow, (row) => String(regionOf(row)))

/** Map an OS name to a device class, or null when it can't be inferred. */
export function inferDeviceTypeFromOs(os: string | null | undefined): 'mobile' | 'desktop' | null {
  const value = (os ?? '').toLowerCase()
  if (!value) return null
  if (value.includes('android') || value.includes('ios')) return 'mobile'
  if (
    value.includes('windows') ||
    value.includes('mac') ||
    value.includes('linux') ||
    value.includes('ubuntu') ||
    value.includes('chrome os')
  ) {
    return 'desktop'
  }
  return null
}

export interface DeviceTypeCount {
  /** Lower-case device class as the API returns it (`desktop`, `mobile`, `tablet`) */
  name: string
  count: number
}

/**
 * Device-type totals. Reads `device_type` rows; only when there are none does
 * it fall back to inferring a class from OS rows. Never both: OS rows describe
 * the same visitors as the device rows, so mixing them double counts.
 * Rows that can't be classified are dropped, not bucketed as "Unknown".
 */
export function aggregateDeviceTypes(aggregate: AggregateRow[] | null | undefined): DeviceTypeCount[] {
  const rows = aggregate ?? []
  const stats = new Map<string, number>()
  const deviceRows = rows.filter((row) => row?.device_type)

  if (deviceRows.length > 0) {
    for (const row of deviceRows) {
      const name = String(row.device_type).toLowerCase()
      if (name === 'unknown') continue
      stats.set(name, (stats.get(name) ?? 0) + toCount(row.count))
    }
  } else {
    for (const row of rows) {
      if (!row?.client_os) continue
      const name = inferDeviceTypeFromOs(row.client_os)
      if (!name) continue
      stats.set(name, (stats.get(name) ?? 0) + toCount(row.count))
    }
  }

  return sortDesc([...stats].map(([name, count]) => ({ name, count })))
}

/**
 * The period's deduped unique-visitor total, the same field the Overview
 * strip reads. Falls back to the top-level `metrics.totalSessions` (also the
 * summary's deduped total). Returns 0 when neither is present, never a row sum.
 */
export function getPeriodUniqueVisitorTotal(metrics: any): number {
  const fromTrends = Number(metrics?.trends?.totalSessions?.[0]?.total_sessions)
  if (Number.isFinite(fromTrends) && fromTrends > 0) return fromTrends
  const fromSummary = Number(metrics?.totalSessions)
  return Number.isFinite(fromSummary) && fromSummary > 0 ? fromSummary : 0
}

/**
 * Share of `total` as a percentage (0–100 is not enforced: when rows overlap,
 * a caller summing shares can see more than 100%). Returns 0 for a
 * non-positive total.
 */
export function shareOf(count: number, total: number): number {
  return total > 0 ? (count / total) * 100 : 0
}

/**
 * Display widths for a stacked bar whose shares were taken against a deduped
 * total. Shares of overlapping rows can add up past 100%, which would overflow
 * the bar, so widths are scaled down proportionally only in that case. Under
 * 100% the widths are the shares themselves and the gap is the uncovered rest.
 */
export function stackedBarWidths(shares: number[]): number[] {
  const sum = shares.reduce((acc, s) => acc + Math.max(0, s), 0)
  if (sum <= 100) return shares.map((s) => Math.max(0, s))
  return shares.map((s) => (Math.max(0, s) / sum) * 100)
}

/**
 * Deduped unique count for a chip selection over page groups (sections).
 *
 * - "all" selected, or every group selected → the endpoint's deduped total.
 * - Exactly one group → that group's deduped value.
 * - Several (but not all) groups → `null`: the per-group values overlap
 *   (a visitor can touch several sections), and the endpoint returns no
 *   deduped figure for a multi-group selection, so there is nothing
 *   defensible to show.
 */
export function uniqueForGroupSelection(
  selected: Iterable<string>,
  groupUniques: Record<string, number>,
  allTotal: number,
): number | null {
  const picked = new Set(selected)
  if (picked.has('all')) return allTotal
  const available = Object.keys(groupUniques)
  if (available.length > 0 && available.every((g) => picked.has(g))) return allTotal
  const present = [...picked].filter((g) => g in groupUniques)
  if (present.length === 1) return toCount(groupUniques[present[0]])
  if (present.length === 0) return 0
  return null
}
